// Carrier-aware tracking links — ONE helper for every screen (Sep 2026).
//
// The two-label workflow ships the kit out on Aramex and brings the sample
// back on Australia Post Express. Every screen used to build an AusPost URL
// from whatever tracking number it had, so Aramex consignments (MP…/AN…)
// opened AusPost and showed "not found".
//
// Resolution order — metadata first, pattern only as a last resort:
//   1. the absolute tracking_url stored on the label when it was created
//      (already carrier-correct; the carrier is read back off its host)
//   2. the carrier / service recorded for that label
//   3. the tracking-number format
//   4. nothing — return no href rather than a misleading link

export const ARAMEX = "aramex";
export const AUSPOST = "auspost";

const CARRIERS = {
  [ARAMEX]: {
    label: "Aramex",
    short: "Aramex",
    url: (n) => `https://www.aramex.com.au/tools/track/?l=${encodeURIComponent(n)}`,
  },
  [AUSPOST]: {
    label: "Australia Post",
    short: "AusPost",
    url: (n) => `https://auspost.com.au/mypost/track/#/details/${encodeURIComponent(n)}`,
  },
};

export const carrierLabel = (carrier) => CARRIERS[carrier]?.label || null;
export const carrierShortLabel = (carrier) => CARRIERS[carrier]?.short || null;

// Aramex Australia (ex-Fastway) domestic consignments: AN/MP + 10 digits.
const ARAMEX_NUMBER = /^(?:MP|AN)\d{8,}$/i;
// AusPost: 33-prefixed eParcel barcodes and the long all-numeric article ids.
const AUSPOST_NUMBER = /^(?:33[A-Z0-9]{14,}|\d{16,})$/i;

function carrierFromName(value) {
  const v = String(value || "").toLowerCase();
  if (!v) return null;
  if (v.includes("aramex") || v.includes("fastway")) return ARAMEX;
  if (v.includes("auspost") || v.includes("australia post") || v.includes("mypost")) return AUSPOST;
  return null;
}

function carrierFromUrl(url) {
  const v = String(url || "").toLowerCase();
  if (!v) return null;
  if (v.includes("aramex.com")) return ARAMEX;
  if (v.includes("auspost.com")) return AUSPOST;
  return null;
}

// Starshipit AusPost products (B20 Express, B30 Standard, …).
const carrierFromService = (code) =>
  /^B\d{2}$/i.test(String(code || "").trim()) ? AUSPOST : null;

const carrierFromNumber = (n) => {
  const t = String(n || "").trim().toUpperCase();
  if (ARAMEX_NUMBER.test(t)) return ARAMEX;
  if (AUSPOST_NUMBER.test(t)) return AUSPOST;
  return null;
};

export function detectCarrier({ carrier, carrierName, serviceCode, trackingUrl,
  trackingNumber } = {}) {
  return carrierFromUrl(trackingUrl)
    || carrierFromName(carrier)
    || carrierFromName(carrierName)
    || carrierFromService(serviceCode)
    || carrierFromNumber(trackingNumber);
}

/** One resolved link. `href` is null when the carrier can't be established —
 *  the caller must then show the number as plain text. */
export function trackingLink({ trackingNumber, trackingUrl, carrier, carrierName,
  serviceCode, kind, label } = {}) {
  const number = String(trackingNumber || "").trim();
  if (!number) return null;
  const resolved = detectCarrier({ carrier, carrierName, serviceCode, trackingUrl,
    trackingNumber: number });
  const stored = /^https?:\/\//i.test(String(trackingUrl || "").trim())
    ? String(trackingUrl).trim() : null;
  const href = stored || (resolved ? CARRIERS[resolved].url(number) : null);
  return {
    kind: kind || null,
    label: label || null,
    number,
    href,
    carrier: resolved,
    carrierLabel: carrierLabel(resolved),
    carrierShortLabel: carrierShortLabel(resolved),
    title: href
      ? `Track ${number} with ${carrierLabel(resolved) || "the carrier"}`
      : `${number} — carrier unknown, no tracking link`,
  };
}

/** Both legs of a booking, in workflow order, skipping legs with no number. */
export function bookingTrackingLinks(booking = {}) {
  const b = booking || {};
  return [
    trackingLink({
      kind: "outbound",
      label: "Kit to customer",
      trackingNumber: b.outbound_tracking_number
        || b.starshipit_tracking_number || b.kit_tracking_number,
      trackingUrl: b.outbound_tracking_url || b.starshipit_tracking_url,
      carrier: b.outbound_carrier,
      carrierName: b.outbound_carrier_name,
      serviceCode: b.outbound_service_code,
    }),
    trackingLink({
      kind: "sample_return",
      label: "Sample back to lab",
      trackingNumber: b.sample_return_tracking_number
        || b.return_tracking_number_ext || b.return_tracking_number,
      trackingUrl: b.sample_return_tracking_url,
      carrier: b.sample_return_carrier,
      carrierName: b.sample_return_carrier_name,
      serviceCode: b.sample_return_service_code,
    }),
  ].filter(Boolean);
}
